/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useHotkeys } from 'react-hotkeys-hook';
import PropTypes from 'prop-types';

// @mui material components
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Icon from '@mui/material/Icon';
import CardMedia from '@mui/material/CardMedia';
import CardContent from '@mui/material/CardContent';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';

const ErTagPrint = forwardRef(({ parent, callback }, ref) => {
  const mod = 'export_request_tag';
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();
  const [attr, setAttr] = useState({});
  const [loading, setLoading] = useState(false);
  const slipRef = useRef(null);

  const handlePrint = useReactToPrint({
    content: () => slipRef.current,
  });

  const print = () => {
    setLoading(true);

    FetchData('GET', `/${mod}/${parent.id}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr(result.data);
        setLoading(false);

        handlePrint();

        callback();
      }
    });
  };

  useImperativeHandle(ref, () => {
    return {
      print,
    };
  });

  const renderOrder = () => {
    return (
      <MDBox mt={0} p={3}>
        {attr.code === undefined ? null : (
          <MDBox align='center'>
            <CardMedia
              sx={{ height: 250, width: 250, margin: 'auto' }}
              image={attr.image}
              title={attr.code}
            />
            <CardContent>
              <MDBox sx={{ fontSize: 30 }}>{attr.code}</MDBox>
            </CardContent>
          </MDBox>
        )}
      </MDBox>
    );
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderPrint = () => {
    return (
      <div style={{ display: 'none' }}>
        <div ref={slipRef}>{renderOrder()}</div>
      </div>
    );
  };

  return (
    <>
      <IconButton
        aria-label='print'
        color={parent.print_no * 1 > 0 ? 'secondary' : 'info'}
        onClick={print}
      >
        <Icon>print</Icon>
      </IconButton>
      {renderLoading()}
      {renderPrint()}
    </>
  );
});

ErTagPrint.propTypes = {
  parent: PropTypes.shape({
    id: PropTypes.string.isRequired,
    print_no: PropTypes.string.isRequired,
  }).isRequired,
  callback: PropTypes.func.isRequired,
};

export default ErTagPrint;
